import React from 'react';
import { Box, Tooltip } from '@leagueplatform/genesis-core';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { MenuItem } from '../../../types/components.types';
import { dropdownMenuItemStyles } from '../select-action-menu.styles';

/**
 * Type definition for Radix UI's DropdownMenu.Item onSelect handler.
 * This handler receives a native DOM Event (not React's SyntheticEvent).
 */
type DropdownMenuItemSelectHandler = (event: Event) => void;

interface SelectActionMenuItemProps {
  item: MenuItem;
  handleModalOpenChange: (item: MenuItem) => void;
}

/**
 * Renders a single menu item (radix DropdownMenu.Item) within the SelectActionMenu component.
 *
 * If the item has a `disabledTooltip`, the item's action will not be fired on click and instead disabled styles and a tooltip explaining why the item is disabled will appear on hover/focus.
 * If the item has a `confirmationModal` property, clicking it will trigger the `handleModalOpenChange` function.
 * Otherwise, it will execute the `action` function associated with the item.
 *
 * @param {MenuItem} item - The menu item configuration object.
 * @param {Function} handleModalOpenChange - A function to handle opening a confirmation modal.
 *
 * @returns {JSX.Element} The rendered menu item component.
 */

export const SelectActionMenuItem = ({
  item,
  handleModalOpenChange,
}: SelectActionMenuItemProps) => {
  const {
    disabledTooltip,
    action,
    label,
    confirmationModal,
    keepOpenOnSelect,
  } = item;

  /**
   * Handler for the menu item selection.
   *
   * Strategy: Let Radix UI close the dropdown naturally (which includes proper cleanup),
   * THEN execute the action. This prevents focus conflicts and state issues.
   *
   * Behavior:
   * 1. If keepOpenOnSelect is true - prevent Radix's auto-close to keep menu open
   *    Action executes immediately since menu stays open
   * 2. Otherwise - allow Radix to close, defer action to next tick
   *    This ensures Radix completes its cleanup (focus restoration, event listeners, etc.)
   *    before the action or modal runs
   *
   * Why defer actions?
   * When actions (especially modal opens) run synchronously in onSelect, they compete with
   * Radix's cleanup process, causing focus conflicts. By deferring to the next tick with
   * setTimeout, we allow Radix to fully complete its close sequence first.
   */
  const handleSelect: DropdownMenuItemSelectHandler = (event) => {
    if (keepOpenOnSelect) {
      // Keep menu open - execute action immediately
      event.preventDefault();
      action?.();
    } else {
      // Let Radix close naturally, then execute action on next tick
      // This allows Radix to complete cleanup before action runs
      setTimeout(() => {
        if (confirmationModal) {
          handleModalOpenChange(item);
        } else {
          action?.();
        }
      }, 0);
    }
  };

  return disabledTooltip ? (
    <Tooltip
      align="center"
      avoidCollisions
      content={disabledTooltip}
      side="left"
      sideOffset={30}
    >
      <DropdownMenu.Item asChild>
        <Box css={dropdownMenuItemStyles}>{label}</Box>
      </DropdownMenu.Item>
    </Tooltip>
  ) : (
    <DropdownMenu.Item asChild onSelect={handleSelect}>
      <Box css={dropdownMenuItemStyles}>{label}</Box>
    </DropdownMenu.Item>
  );
};
